import { FC } from 'react';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import useTheme from '@mui/material/styles/useTheme';
import LocationOffOutlined from '@mui/icons-material/LocationOffOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import ApartmentOutlined from '@mui/icons-material/ApartmentOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import SupportAgentOutlined from '@mui/icons-material/SupportAgentOutlined';
import { Text } from '@concepta/react-material-ui';
import { isMobile } from 'utils/isMobile';
import { alpha } from '@mui/material/styles';
import { IconButton } from '@mui/material';

export interface RestrictedLocationModalProps {
  open: boolean;
  onClose: () => void;
  onContactSupport: () => void;
  locationName: string;
}

const RestrictedLocationModal: FC<RestrictedLocationModalProps> = ({
  open,
  onClose,
  onContactSupport,
  locationName,
}) => {
  const theme = useTheme();

  return (
    <Dialog onClose={onClose} open={open} fullWidth maxWidth="sm">
      <Box position="absolute" right={16} top={16}>
        <IconButton edge="start" onClick={onClose} aria-label="close">
          <CloseOutlined
            sx={{
              color: 'primary.darker',
            }}
          />
        </IconButton>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        p={isMobile ? 2 : 3}
        gap={3}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={64}
          height={64}
          borderRadius="50%"
          bgcolor={alpha(theme.palette.error.main, 0.1)}
        >
          <LocationOffOutlined sx={{ fontSize: 32, color: 'error.main' }} />
        </Box>

        <Text
          fontSize={20}
          fontWeight={700}
          color="primary.darker"
          textAlign="center"
        >
          Location Not Available
        </Text>

        <Text
          fontSize={14}
          fontWeight={400}
          color="text.secondary"
          textAlign="center"
          px={isMobile ? 0 : 2}
        >
          We apologize for the inconvenience, but we are unable to deliver to
          this location due to local restrictions. We recommend selecting an
          alternative address nearby where we can provide delivery services.
        </Text>

        <Box
          bgcolor="grey.100"
          borderRadius={isMobile ? '12px' : '16px'}
          p={2}
          width="100%"
          display="flex"
          gap={2}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={40}
            height={40}
            borderRadius="8px"
            bgcolor={alpha(theme.palette.grey[800], 0.1)}
          >
            <ApartmentOutlined
              sx={{ fontSize: 24, color: 'text.secondary', flexShrink: 0 }}
            />
          </Box>
          <Box display="flex" flexDirection="column">
            <Text fontSize={14} fontWeight={700} color="text.primary">
              {locationName}
            </Text>
          </Box>
        </Box>

        <Box width="100%" display="flex" flexDirection="column" gap={2}>
          <Text fontSize={14} fontWeight={600} color="text.primary">
            What you can do:
          </Text>

          <Box display="flex" gap={2} alignItems="flex-start">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width={32}
              height={32}
              borderRadius="50%"
              bgcolor={alpha(theme.palette.info.main, 0.1)}
              flexShrink={0}
            >
              <SearchOutlined sx={{ fontSize: 18, color: 'info.main' }} />
            </Box>
            <Box display="flex" flexDirection="column">
              <Text
                fontSize={theme.typography.body2.fontSize}
                fontWeight={500}
                color="text.primary"
              >
                Check nearby locations
              </Text>
              <Text
                fontSize={theme.typography.caption.fontSize}
                fontWeight={400}
                color="text.secondary"
              >
                We may serve other resorts in the area
              </Text>
            </Box>
          </Box>

          <Box display="flex" gap={2} alignItems="flex-start">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width={32}
              height={32}
              borderRadius="50%"
              bgcolor={alpha(theme.palette.secondary.main, 0.1)}
              flexShrink={0}
            >
              <SupportAgentOutlined
                sx={{ fontSize: 18, color: 'secondary.main' }}
              />
            </Box>
            <Box display="flex" flexDirection="column">
              <Text
                fontSize={theme.typography.body2.fontSize}
                fontWeight={500}
                color="text.primary"
              >
                Contact support
              </Text>
              <Text
                fontSize={theme.typography.caption.fontSize}
                fontWeight={400}
                color="text.secondary"
              >
                Our team can help with alternative solutions
              </Text>
            </Box>
          </Box>
        </Box>

        <Box width="100%" display="flex" flexDirection="column" gap={2} mt={1}>
          <Button variant="contained" fullWidth onClick={onContactSupport}>
            Contact Support
          </Button>

          <Button variant="outlined" fullWidth onClick={onClose}>
            Close and Select Other Address
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default RestrictedLocationModal;
